/**
 * Measure whether an attribute blocks vertically or horizontally on the merchant's own
 * wall. Verticality = column stability (each value holds one x-interval top to bottom)
 * x x-exclusivity (sibling values tile x rather than share it); either term alone
 * crowns a near-constant. Measured per scope, and a scope the attribute does not split
 * does not vote. Input must be the base pog, never a layout this pipeline produced.
 */
import type { StudioItem, StudioRunGeometry } from "./studioTypes";
import { isPlaceholderValue } from "./studioTypes";

/** At or above this an attribute is columnar, at or below it banded. Between the
 *  two the evidence does not decide and a human has to — see `warning`. */
export const VERTICAL_SCORE = 0.5;
export const HORIZONTAL_SCORE = 0.3;

export type BlockRole = "vertical" | "horizontal" | "ambiguous";

export interface ValueBlocking {
  value: string;
  /** Mean adjacent-row x-interval overlap, 0-1. 1 = the interval never moves. */
  stability: number;
  /** Share of the scope's shelves the value reaches, 0-1. */
  coverage: number;
  /** Total placed width, cm — this value's weight in the roll-up. */
  widthCm: number;
  rows: number;
}

export interface ScopeBlocking {
  run: number;
  path: readonly string[];
  /** 1 = sibling values tile x, 0 = they sit on top of each other. */
  exclusivity: number;
  /** Width-weighted mean of the values' column stability. */
  stability: number;
  /** exclusivity x stability. */
  score: number;
  widthCm: number;
  values: readonly ValueBlocking[];
}

export interface BlockingReading {
  attr: string;
  role: BlockRole;
  /** Width-weighted mean of the scopes' scores. */
  score: number;
  /** Spread of the scope scores; a split attribute is a ruling, not a default. */
  disagreement: number;
  /** Scopes the attribute actually splits. None = inert, and it holds no role. */
  scopes: readonly ScopeBlocking[];
  values: readonly ValueBlocking[];
  warning?: string;
}

interface Interval {
  min: number;
  max: number;
}

const spanOverlap = (a: Interval, b: Interval): number =>
  Math.max(0, Math.min(a.max, b.max) - Math.max(a.min, b.min));

const intervalsByRow = (
  placements: readonly { shelf: number; xCm: number; widthCm: number }[],
): Map<number, Interval> => {
  const rows = new Map<number, Interval>();
  for (const placement of placements) {
    const row = rows.get(placement.shelf);
    const min = placement.xCm;
    const max = placement.xCm + placement.widthCm;
    if (row) {
      row.min = Math.min(row.min, min);
      row.max = Math.max(row.max, max);
    } else rows.set(placement.shelf, { min, max });
  }
  return rows;
};

/** Mean row width of a value: the denominator for how much x-stripe it holds. */
const meanRowWidth = (rows: ReadonlyMap<number, Interval>): number => {
  let total = 0;
  for (const row of rows.values()) total += row.max - row.min;
  return rows.size ? total / rows.size : 0;
};

const spanOf = (rows: ReadonlyMap<number, Interval>): Interval => {
  const span: Interval = { min: Infinity, max: -Infinity };
  for (const row of rows.values()) {
    span.min = Math.min(span.min, row.min);
    span.max = Math.max(span.max, row.max);
  }
  return span;
};

/** The items under one parent path, on one wall. */
export interface BlockingScope {
  run: number;
  path: readonly string[];
  items: readonly StudioItem[];
}

const measureScope = (
  scope: BlockingScope,
  attr: string,
  run: StudioRunGeometry,
): ScopeBlocking | null => {
  const ranks = [...run.shelves].map((shelf) => shelf.rank).sort((a, b) => a - b);
  const byValue = new Map<string, { shelf: number; xCm: number; widthCm: number }[]>();
  for (const item of scope.items) {
    const value = String(item.attrs[attr] ?? "");
    if (isPlaceholderValue(value)) continue;
    for (const placement of item.placements) {
      if (placement.run !== run.run) continue;
      let bucket = byValue.get(value);
      if (!bucket) {
        bucket = [];
        byValue.set(value, bucket);
      }
      bucket.push(placement);
    }
  }
  if (byValue.size < 2) return null;

  // Coverage is against the scope's own rows, not the whole wall.
  const scopeRows = new Set<number>();
  for (const placements of byValue.values())
    for (const placement of placements) scopeRows.add(placement.shelf);
  const scopeRanks = ranks.filter((rank) => scopeRows.has(rank));

  const values: ValueBlocking[] = [];
  const shapes: { rows: Map<number, Interval>; widthCm: number }[] = [];
  for (const [value, placements] of byValue) {
    const rows = intervalsByRow(placements);
    const widthCm = placements.reduce((total, placement) => total + placement.widthCm, 0);
    // Adjacent rows of the scope, not of the value: a skipped shelf must count against it.
    let pairs = 0;
    let overlapTotal = 0;
    for (let index = 0; index + 1 < scopeRanks.length; index += 1) {
      const above = rows.get(scopeRanks[index]);
      const below = rows.get(scopeRanks[index + 1]);
      if (!above && !below) continue;
      pairs += 1;
      if (!above || !below) continue;
      const smaller = Math.min(above.max - above.min, below.max - below.min);
      overlapTotal += smaller > 0 ? Math.min(1, spanOverlap(above, below) / smaller) : 1;
    }
    values.push({
      value,
      stability: pairs > 0 ? overlapTotal / pairs : 0,
      coverage: scopeRanks.length ? rows.size / scopeRanks.length : 0,
      widthCm,
      rows: rows.size,
    });
    shapes.push({ rows, widthCm });
  }

  // Exclusivity: how much of the narrower sibling's x-stripe the other one takes.
  let shared = 0;
  let paired = 0;
  for (let i = 0; i < shapes.length; i += 1) {
    for (let j = i + 1; j < shapes.length; j += 1) {
      const a = shapes[i];
      const b = shapes[j];
      const smaller = Math.min(meanRowWidth(a.rows), meanRowWidth(b.rows));
      if (!(smaller > 0)) continue;
      // Width-weighted, or a long tail of slivers outvotes the blocks.
      const weight = Math.min(a.widthCm, b.widthCm);
      shared += weight * Math.min(1, spanOverlap(spanOf(a.rows), spanOf(b.rows)) / smaller);
      paired += weight;
    }
  }
  const exclusivity = paired > 0 ? 1 - shared / paired : 1;

  const voting = values.filter((value) => value.rows > 1);
  const weight = voting.reduce((total, value) => total + value.widthCm, 0);
  const stability = weight
    ? voting.reduce((total, value) => total + value.widthCm * value.stability, 0) / weight
    : 0;
  return {
    run: run.run,
    path: scope.path,
    exclusivity: +exclusivity.toFixed(3),
    stability: +stability.toFixed(3),
    score: +(exclusivity * stability).toFixed(3),
    widthCm: values.reduce((total, value) => total + value.widthCm, 0),
    values: [...values].sort((a, b) => b.widthCm - a.widthCm),
  };
};

/** Split a set of scopes by an attribute — how the caller descends the tree. */
export const splitBlockingScopes = (
  scopes: readonly BlockingScope[],
  attr: string,
): BlockingScope[] =>
  scopes.flatMap((scope) => {
    const groups = new Map<string, StudioItem[]>();
    for (const item of scope.items) {
      const value = String(item.attrs[attr] ?? "");
      const group = groups.get(value);
      if (group) group.push(item);
      else groups.set(value, [item]);
    }
    return [...groups.entries()].map(([value, items]) => ({
      run: scope.run,
      path: [...scope.path, value],
      items,
    }));
  });

/** One scope per run represented by an item's placements. Placeless items fall back to
 *  their source run so a caller with no geometry still gets a scope. */
export const rootBlockingScopes = (
  items: readonly StudioItem[],
  runs: readonly StudioRunGeometry[],
): BlockingScope[] => {
  const byRun = new Map<number, StudioItem[]>();
  for (const item of items) {
    const itemRuns = new Set(item.placements.map((placement) => placement.run));
    if (itemRuns.size === 0) itemRuns.add(item.run);
    for (const itemRun of itemRuns) {
      let bucket = byRun.get(itemRun);
      if (!bucket) {
        bucket = [];
        byRun.set(itemRun, bucket);
      }
      bucket.push(item);
    }
  }
  for (const run of runs) if (!byRun.has(run.run)) byRun.set(run.run, []);
  return [...byRun.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([run, scopeItems]) => ({ run, path: [], items: scopeItems }));
};

export const measureBlockingOf = (
  items: readonly StudioItem[],
  attr: string,
  runs: readonly StudioRunGeometry[],
  scopes: readonly BlockingScope[] = rootBlockingScopes(items, runs),
): BlockingReading => {
  const byRun = new Map(runs.map((run) => [run.run, run]));
  const measured: ScopeBlocking[] = [];
  for (const scope of scopes) {
    const run = byRun.get(scope.run);
    if (!run) continue;
    const reading = measureScope(scope, attr, run);
    if (reading) measured.push(reading);
  }
  const weight = measured.reduce((total, scope) => total + scope.widthCm, 0);
  const score = weight
    ? measured.reduce((total, scope) => total + scope.widthCm * scope.score, 0) / weight
    : 0;
  const mean = measured.length
    ? measured.reduce((total, scope) => total + scope.score, 0) / measured.length
    : 0;
  const disagreement = measured.length
    ? Math.sqrt(
        measured.reduce((total, scope) => total + (scope.score - mean) ** 2, 0) / measured.length,
      )
    : 0;
  const role: BlockRole =
    measured.length === 0
      ? "ambiguous"
      : score >= VERTICAL_SCORE
        ? "vertical"
        : score <= HORIZONTAL_SCORE
          ? "horizontal"
          : "ambiguous";
  return {
    attr,
    role,
    score: +score.toFixed(3),
    disagreement: +disagreement.toFixed(3),
    scopes: measured,
    values: measured.flatMap((scope) => scope.values),
    warning:
      role === "ambiguous"
        ? measured.length === 0
          ? `"${attr}" splits no scope of the base pog — it blocks neither vertically nor horizontally and must not spend a level.`
          : `"${attr}" scores ${score.toFixed(2)}, between banded (${HORIZONTAL_SCORE}) and columnar (${VERTICAL_SCORE}). A wrong default here inverts the level roles; rule on it.`
        : undefined,
  };
};

/** Bandedness places cdt2: not-columnar does not mean banded (an attribute can merely
 *  sequence packs along the shelf), so cdt2 takes the attribute whose values own
 *  disjoint runs of shelves inside their parent's column. */
export interface BandednessReading {
  attr: string;
  /** Width-weighted share of sibling pairs holding disjoint shelf sets, 0-1. */
  score: number;
  /** Scopes the attribute splits. */
  scopes: number;
  /** Mean run-length of a value's shelves; 1 = it never spans a band. */
  contiguity: number;
}

const shelvesOf = (items: readonly StudioItem[], run: number): Set<number> => {
  const shelves = new Set<number>();
  for (const item of items)
    for (const placement of item.placements)
      if (placement.run === run) shelves.add(placement.shelf);
  return shelves;
};

export const measureBandednessOf = (
  items: readonly StudioItem[],
  attr: string,
  runs: readonly StudioRunGeometry[],
  scopes: readonly BlockingScope[] = rootBlockingScopes(items, runs),
): BandednessReading => {
  let disjoint = 0;
  let paired = 0;
  let contiguous = 0;
  let counted = 0;
  let splitScopes = 0;
  for (const scope of scopes) {
    const groups = new Map<string, StudioItem[]>();
    for (const item of scope.items) {
      const value = String(item.attrs[attr] ?? "");
      if (isPlaceholderValue(value)) continue;
      const group = groups.get(value);
      if (group) group.push(item);
      else groups.set(value, [item]);
    }
    if (groups.size < 2) continue;
    splitScopes += 1;
    const shapes = [...groups.values()].map((group) => ({
      shelves: shelvesOf(group, scope.run),
      widthCm: group.reduce(
        (total, item) =>
          total +
          item.placements
            .filter((placement) => placement.run === scope.run)
            .reduce((width, placement) => width + placement.widthCm, 0),
        0,
      ),
    }));
    for (const shape of shapes) {
      if (shape.shelves.size === 0) continue;
      // One contiguous run of shelves = one band. Two runs = a split block.
      const ranks = [...shape.shelves].sort((a, b) => a - b);
      let runsOfShelves = 1;
      for (let index = 1; index < ranks.length; index += 1)
        if (ranks[index] !== ranks[index - 1] + 1) runsOfShelves += 1;
      contiguous += shape.widthCm * (1 / runsOfShelves);
      counted += shape.widthCm;
    }
    for (let i = 0; i < shapes.length; i += 1) {
      for (let j = i + 1; j < shapes.length; j += 1) {
        const a = shapes[i];
        const b = shapes[j];
        const smaller = Math.min(a.shelves.size, b.shelves.size);
        if (smaller === 0) continue;
        const shared = [...a.shelves].filter((shelf) => b.shelves.has(shelf)).length;
        const weight = Math.min(a.widthCm, b.widthCm);
        disjoint += weight * (1 - shared / smaller);
        paired += weight;
      }
    }
  }
  return {
    attr,
    score: paired > 0 ? +(disjoint / paired).toFixed(3) : 0,
    scopes: splitScopes,
    contiguity: counted > 0 ? +(contiguous / counted).toFixed(3) : 0,
  };
};
